/**
 * Aplicación web Directorio del Ayuntamiento de Zamora.
 * 
 * Desarrollo e implementación: Jaime León Pereira.
 * Ingeniero Informático de la Unidad TIC.
 */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import ConfirmModal from "./ConfirmModal";
import AlertModal from "../common/AlertModal";
import { Modal, Button, Form } from "react-bootstrap";
import useVersionMovil from "../../hooks/useMobileVersion";
import "../../styles/AdminEntities.css";

/**
 * Componente de administración de edificios.
 * Permite listar, filtrar, crear, editar y eliminar edificios,
 * así como gestionar la subida y eliminación de fotografías.
 * La autenticación se implementa mediante Microsoft Entra (MSAL).
 */
const AdminEdificios = () => {

  /* ============================
     Estado principal de datos
     ============================ */
  const isMobile = useVersionMovil();
  // Lista de edificios obtenidos desde la API
  const [edificios, setEdificios] = useState([]);

  // Referencia al input de archivo para poder resetearlo manualmente
  const fileInputRef = useRef(null);

  /* ============================
     Estado de modales generales
     ============================ */
  
  // Modal de confirmación para eliminación de edificios
  const [modalOpen, setModalOpen] = useState(false);
  
  // Edificio seleccionado para eliminar
  const [edificioToDelete, setEdificioToDelete] = useState(null);

  // Modal del formulario de creación / edición
  const [formModalOpen, setFormModalOpen] = useState(false);

  // Modo del formulario: crear | editar
  const [modo, setModo] = useState("crear");

  /* ============================
     Estado de alertas
     ============================ */

  // Controla la visibilidad del modal de alertas
  const [alertOpen, setAlertOpen] = useState(false);

  // Mensaje mostrado en la alerta
  const [alertMessage, setAlertMessage] = useState("");

  // Tipo de alerta: success | error
  const [alertType, setAlertType] = useState("success");

  /* ============================
     Estado de gestión de fotos
     ============================ */
  
  // Modal de confirmación para eliminar la foto actual
  const [confirmDeleteFotoModal, setConfirmDeleteFotoModal] = useState(false);

  // Indica si la foto debe eliminarse al guardar
  const [deleteFoto, setDeleteFoto] = useState(false);

  /* ============================
     Estado del edificio en edición
     ============================ */
  
  // Datos del edificio que se está creando o editando   
  const [edificioActual, setEdificioActual] = useState({
    nombre: "",
    direccion: "",
    telefono: "",
  });

  /* ============================
     Autenticación (MSAL)
     ============================ */
  
  
  const { instance, accounts } = useMsal();

   /**
   * Muestra un mensaje de alerta reutilizable.
   */
  const openAlert = (msg, type = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setAlertOpen(true);
  };

  /* ============================
     Estado de filtros de búsqueda
     ============================ */

  const [filtros, setFiltros] = useState({
    nombre: "",
    direccion: "",
    telefono: "",
  });

  /**
   * Obtiene de forma silenciosa el access token de Microsoft Entra.
   * Necesario para autenticar las peticiones a la API.
   */
  const getAccessToken = async () => {
    if (!accounts || accounts.length === 0) return null;
    try {
      const response = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [process.env.REACT_APP_API_SCOPE],
      });
      return response.accessToken;
    } catch (error) {
      console.error("Error obteniendo access token:", error);
      return null;
    }
  };

  /**
   * Obtiene la lista de edificios desde la API,
   * aplicando los filtros actuales si existen.
   */
  const fetchEdificios = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const params = { ...filtros };
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/edificios`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      const data = Array.isArray(res.data) ? res.data : res.data.content || [];

      const dataOrdenada = data.sort((a, b) =>
        a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
      );

      setEdificios(data);
    } catch (err) {
      console.error("Error al obtener edificios:", err);
      openAlert("Error cargando edificios desde la API", "error");
    }
  };

  /**
   * Carga inicial de edificios al montar el componente.
   */
  useEffect(() => {
    fetchEdificios();
  }, []);

  /**
   * Si se limpian todos los filtros, se recarga la lista completa.
   */
  useEffect(() => {
    if (!filtros.nombre && !filtros.direccion && !filtros.telefono) {
      fetchEdificios();
    }
  }, [filtros]);

  /**
   * Actualiza filtros de texto.
   */
  const handleFiltroChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
  };

  /**
   * Actualiza filtros numéricos evitando caracteres no válidos.
   */
  const handleFiltroChangeNum = (name, value) => {
    setFiltros((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Ejecuta la búsqueda aplicando filtros.
   */
  const handleBuscar = (e) => {
    e.preventDefault();
    fetchEdificios();
  };

  /**
   * Inicializa el formulario en modo creación.
   */
  const handleCrear = () => {
    setModo("crear");
    setEdificioActual({ nombre: "", direccion: "", telefono: "" });
    setDeleteFoto(false);
    setFormModalOpen(true);
  };

  /**
   * Inicializa el formulario en modo edición.
   */
  const handleEditar = (edificio) => {
    setModo("editar");
    setEdificioActual(edificio);
    setDeleteFoto(false);
    setFormModalOpen(true);
  };

  /**
   * Guarda los cambios del edificio (crear o editar),
   * gestionando también la subida o eliminación de la foto.
   */
  const handleGuardar = async (e) => {
    e.preventDefault();
    try {
      const token = await getAccessToken();
      
      if (!token) {
        openAlert("No se pudo obtener el token. Inicia sesión de nuevo.", "error");
        return;
      }
      
      
      if (!token) return;

      let response;

      if (modo === "crear") {
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/edificios`,
          {
            nombre: edificioActual.nombre,
            direccion: edificioActual.direccion,
            telefono: edificioActual.telefono
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.put(
          `${process.env.REACT_APP_API_URL}/edificios/${edificioActual.id}`,
          {
            nombre: edificioActual.nombre,
            direccion: edificioActual.direccion,
            telefono: edificioActual.telefono
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      const edificioGuardado = response.data;

      // Eliminar foto si se ha marcado para borrado
      if (deleteFoto) {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/edificios/${edificioGuardado.id}/foto`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // Subir nueva foto si se ha seleccionado
      if (edificioActual.fotoFile) {
        const formData = new FormData();
        formData.append("foto", edificioActual.fotoFile);

        await axios.put(
          `${process.env.REACT_APP_API_URL}/edificios/${edificioGuardado.id}/foto`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      openAlert(modo === "crear" ? "Edificio creado correctamente" : "Cambios guardados", "success");
      setFormModalOpen(false);
      fetchEdificios();
      setDeleteFoto(false);
    } catch (err) {
      console.error("Error al guardar edificio:", err);
      openAlert("Error guardando edificio", "error");
    }
  };

  /**
   * Abre el modal de confirmación de borrado.
   */
  const handleDelete = (ed) => {
    setEdificioToDelete(ed);
    setModalOpen(true);
  };

  /**
   * Elimina definitivamente el edificio seleccionado.
   */
  const handleConfirmDelete = async () => {
    if (!edificioToDelete) return;

    try {
      const token = await getAccessToken();
      if (!token) return;

      await axios.delete(`${process.env.REACT_APP_API_URL}/edificios/${edificioToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      openAlert("Edificio eliminado correctamente", "success");
      fetchEdificios();
    } catch (err) {
      console.error(err);
      openAlert("Error eliminando edificio", "error");
    } finally {
      setModalOpen(false);
      setEdificioToDelete(null);
    }
  };
  
  /**
   * Marca la foto para ser eliminada al guardar cambios.
   */
  const confirmarEliminarFoto = () => {
    setDeleteFoto(true);
    setEdificioActual((prev) => ({ ...prev, fotoFile: null, fotoUrl: null }));
    if (fileInputRef.current) fileInputRef.current.value = "";
    setConfirmDeleteFotoModal(false);
  };

  /**
   * Cancela la eliminación de la foto.
   */
  const cancelarEliminarFoto = () => {
    setConfirmDeleteFotoModal(false);
  };

  /*************************************************************
   * Renderizado de la interfaz de administración de edificios.*
   *************************************************************/
  return (
    /* Contenedor principal de la vista de administración */
    <div className="admin-personas-container">
      {/* Título de la sección */}
      <div className="text-center fw-bold fs-4">Gestión de Edificios</div>

      {/* =============================
          Bloque de búsqueda y filtros
          ============================= */}
      <div className="buscador-card p-3 mb-3 me-2">
        <form onSubmit={handleBuscar}>

          {/* Campos de filtrado por nombre, dirección y teléfono */}
          <div className={`filtros-grid ${isMobile ? 'filtros-mobile' : ''} mb-3`}>
            <input type="text" name="nombre" placeholder="Nombre" value={filtros.nombre} onChange={handleFiltroChange} maxLength={50} />
            <input type="text" name="direccion" placeholder="Dirección" value={filtros.direccion} onChange={handleFiltroChange} maxLength={60} />
            <input
              type="text"
              name="telefono"
              placeholder="Teléfono"
              value={filtros.telefono}
              maxLength={9}
              inputMode="numeric"
              pattern="[0-9]*"
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                handleFiltroChangeNum("telefono", value);
              }}
            />
            </div>

          {/* Botonera de acciones del buscador */}
          <div className="botones-buscador">
            <button type="submit" className="btn-modern btn-modern-primary">Buscar</button>
            <button type="button" className="btn-modern btn-modern-secondary" onClick={() => setFiltros({ nombre: "", direccion: "", telefono: "" })}>Mostrar todo</button>
            {/* Abre el formulario para crear un nuevo edificio */}
            <button type="button" className="btn-modern btn-modern-create" onClick={handleCrear}><i className="bi bi-plus-circle"></i> Añadir edificio</button>
          </div>
        </form>
      </div>

      {/* =============================
              Tabla de resultados
          ============================= */}
      <div className="table-scroll-wrapper">
        {/* Contenedor con scroll horizontal */}
        <div className="table-scroll">
          <table className="table-modern">
            
            {/* Cabecera de la tabla */}
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Dirección</th>
                <th>Teléfono</th>
                <th>Foto</th>
                <th>Acciones</th>
              </tr>
            </thead>
            
            {/* Cuerpo de la tabla con los edificios obtenidos */}
            <tbody>
              {edificios.map((ed) => (
                <tr key={ed.id}>
                  <td>{ed.id}</td>
                  <td>{ed.nombre}</td>
                  <td>{ed.direccion}</td>
                  <td>{ed.telefono}</td>
                  <td className="foto-cell">
                    {ed.fotoUrl ? <i className="bi bi-check-circle-fill text-success"></i> : <i className="bi bi-x-circle-fill text-danger"></i>}
                  </td>

                  {/* Acciones disponibles para cada edificio */}
                  <td className="actions-cell">
                    
                    {/* Editar edificio */}
                    <button type="button" className="btn-action btn-warning" title="Editar" onClick={() => handleEditar(ed)}><i className="bi bi-pencil"></i></button>
                    
                    {/* Eliminar edificio */}
                    <button type="button" className="btn-action btn-danger" title="Eliminar" onClick={() => handleDelete(ed)}><i className="bi bi-trash"></i></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* =============================
            Modales de la aplicación
          ============================= */}

      {/* Modal de confirmación para eliminación de edificios */}
      <ConfirmModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onConfirm={handleConfirmDelete} message={`¡Atención! Si eliminas el edificio "${edificioToDelete?.nombre}" se eliminará este campo de todos los trabajadores asociados. ¿Desea continuar?`} />

      {/* Modal de alertas para feedback al usuario */}
      <AlertModal show={alertOpen} message={alertMessage} type={alertType} onClose={() => setAlertOpen(false)} />
      
      {/* Modal de creación y edición de edificios */}
      <Modal 
        show={formModalOpen} 
        onHide={() => setFormModalOpen(false)}
        backdrop="static"
      >
        <Form onSubmit={handleGuardar}>
          <Modal.Header style={{ backgroundColor: "#3b82f6", color: "white" }}>
            <Modal.Title>
              {modo === "crear" ? "Crear edificio" : "Editar edificio"}
            </Modal.Title>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setFormModalOpen(false)}
            ></button>
          </Modal.Header>

          <Modal.Body>
            {/* Nombre */}
            <Form.Group className="mb-3">
              <Form.Label>Nombre <span style={{color:"red"}}>*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej: Casa Consistorial"
                value={edificioActual.nombre}
                onChange={(e) =>
                  setEdificioActual({ ...edificioActual, nombre: e.target.value })
                }
                required
                maxLength={50}
              />
            </Form.Group>

            {/* Dirección */}
            <Form.Group className="mb-3">
              <Form.Label>Dirección <span style={{color:"red"}}>*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej: Plaza Mayor, S/N"
                value={edificioActual.direccion}
                onChange={(e) =>
                  setEdificioActual({ ...edificioActual, direccion: e.target.value })
                }
                required
                maxLength={50}
              />
            </Form.Group>

            {/* Teléfono */}
            <Form.Group className="mb-3">
              <Form.Label>Teléfono <span style={{color:"red"}}>*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej: 980000001"
                value={edificioActual.telefono}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setEdificioActual({
                    ...edificioActual,
                    telefono: value
                  });
                }}
                maxLength={9}
                inputMode="numeric"
                pattern="[0-9]*"
                required
              />
            </Form.Group>


            {/* Foto */}
            <Form.Group className="mb-3">
              <Form.Label>Foto (opcional)</Form.Label>
              <Form.Control
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={(e) =>
                  setEdificioActual({
                    ...edificioActual,
                    fotoFile: e.target.files[0],
                  })
                }
              />
            </Form.Group>

            {/* Previsualización si se ha subido nueva foto */}
            {edificioActual.fotoFile && (
              <div className="mt-2 text-center">
                <img
                  src={URL.createObjectURL(edificioActual.fotoFile)}
                  alt="Vista previa"
                  style={{
                    width: "100%",
                    maxWidth: "200px",
                    borderRadius: "8px",
                    objectFit: "cover",
                  }}
                />
                <br />
                <Button
                  type="button" 
                  variant="outline-danger"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    setEdificioActual({
                      ...edificioActual,
                      fotoFile: null,
                    });
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  Eliminar foto
                </Button>
              </div>
            )}

            {/* Mostrar foto actual si existe y no se ha marcado para eliminar */}
            {modo === "editar" && !edificioActual.fotoFile && edificioActual.fotoUrl && !deleteFoto && (
              <div className="mt-2 text-center">
                <img
                  src={`${process.env.REACT_APP_URL}${edificioActual.fotoUrl}`}
                  alt="Foto actual"
                  style={{
                    width: "100%",
                    maxWidth: "200px",
                    borderRadius: "8px",
                    objectFit: "cover",
                  }}
                />
                <br />
                <button
                  type="button"
                  className="btn btn-danger mt-2"
                  onClick={() => setConfirmDeleteFotoModal(true)} // solo abre modal de confirmación
                >
                  <i className="bi bi-trash"></i> Eliminar foto
                </button>
              </div>
            )}

          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setFormModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit">
              {modo === "crear" ? "Crear" : "Guardar cambios"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal de confirmación de borrado de foto */}
      <Modal
        show={confirmDeleteFotoModal}
        onHide={cancelarEliminarFoto}
        backdrop="static"
        keyboard={false}
        centered
        className={`${confirmDeleteFotoModal ? "sombreado" : ""}`}
      >
        <Modal.Header className="bg-danger text-white">
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Confirmar eliminación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="fs-6">
            ¿Seguro que deseas eliminar la foto?
          </p>
          <p className="text-muted small">
            La imagen se eliminará definitivamente al guardar los cambios.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelarEliminarFoto}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmarEliminarFoto}>
            <i className="bi bi-trash me-1"></i> Eliminar foto
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminEdificios;
